import { useEffect, useRef } from "react";
import { notificationsWebSocketConnection } from "@uhg-ecp/ecp-notifications-sc-subscriber";
import { processNotificationMessage } from "../masthead/processNotificationMessage";
import { useStopFunctionStore } from "../store/stopFunctionStore";
import { useSubscriptionIdStore } from "../store/subscriptionIdStore";
import { useFilterStore } from "../store/filterStore";
import { useConnectionStore } from "../store/connectionStore";
import { createLogger } from "../utils/logger";
import { useAppDispatch } from "../../../redux/hooks";
import { invalidateTemplateHtmlCacheEntry } from "../../../redux/utils";
import { getToken } from "../../../config/getToken";

const logger = createLogger("useNotificationsWebSocketConnection");

const WS_CONNECTED_KEY = "ecp_ws_connected";
const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY_MS = 5000;
const CONNECTION_TIMEOUT_MS = 10000;

interface StoredConnectionState {
  connected: boolean;
  tokenPrefix: string;
}

function isConnected(token?: string): boolean {
  try {
    const stored = localStorage.getItem(WS_CONNECTED_KEY);
    if (!stored) return false;
    const { connected, tokenPrefix } = JSON.parse(stored) as StoredConnectionState;
    return connected === true && tokenPrefix === token?.substring(0, 20);
  } catch {
    return false;
  }
}

function markConnected(token?: string): void {
  try {
    localStorage.setItem(
      WS_CONNECTED_KEY,
      JSON.stringify({ connected: true, tokenPrefix: token?.substring(0, 20) }),
    );
  } catch (err) {
    logger.warn("Failed to mark connection:", err);
  }
}

function clearConnected(): void {
  try {
    localStorage.removeItem(WS_CONNECTED_KEY);
  } catch (err) {
    logger.warn("Failed to clear connection flag:", err);
  }
}

interface SubscriptionVariable {
  filters?: string[];
  [key: string]: unknown;
}

interface UseNotificationsWebSocketConnectionOptions {
  env?: string;
  subscriptionVariable: SubscriptionVariable;
  setWsReady: (ready: boolean) => void;
  enabled?: boolean;
}

interface NotificationDataMessage {
  payload?: {
    data?: {
      onNotificationPublished?: {
        message?: string;
        title?: string;
        createdBy?: string;
      };
    };
  };
}

function getConnectionSignature(
  token: string | undefined,
  env: string | undefined,
  subscriptionVariable: SubscriptionVariable,
): string {
  const normalizedFilters = Array.isArray(subscriptionVariable.filters)
    ? Array.from(
        new Set(
          subscriptionVariable.filters
            .filter((value): value is string => typeof value === "string")
            .map((value) => value.trim())
            .filter(Boolean),
        ),
      ).sort()
    : [];

  return JSON.stringify({
    env: env || "",
    tokenPrefix: token?.substring(0, 20) || "",
    recipientIdAttribute: subscriptionVariable.recipientIdAttribute ?? null,
    recipientIdpAttribute: subscriptionVariable.recipientIdpAttribute ?? null,
    filters: normalizedFilters,
  });
}

export function useNotificationsWebSocketConnection({
  env,
  subscriptionVariable,
  setWsReady,
  enabled = true,
}: UseNotificationsWebSocketConnectionOptions): void {
  const dispatch = useAppDispatch();
  const setStopFunction = useStopFunctionStore((state) => state.setStopFunction);
  const setSubscriptionId = useSubscriptionIdStore((state) => state.setSubscriptionId);
  const setFilters = useFilterStore((state) => state.setFilters);
  const setConnectedElement = useConnectionStore((state) => state.setConnectedElement);
  const isPageUnloadingRef = useRef(false);
  const hadTokenRef = useRef(false);
  const activeConnectionSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let subscriber: { stop: () => void } | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let reconnectAttempts = 0;
    let isEffectActive = true;
    let isInitializing = false;

    const initializeConnection = async () => {
      if (!isEffectActive || isInitializing) {
        return;
      }

      const connectionToken = await getToken();
      const connectionSignature = getConnectionSignature(connectionToken ?? undefined, env, subscriptionVariable);

      
      const hasExistingConnection = isConnected(connectionToken ?? undefined);
      
      // if (hasExistingConnection) {
      //  // logger.info("Reusing existing WebSocket connection");
      // }

      if (hasExistingConnection && activeConnectionSignatureRef.current === connectionSignature) {
        setWsReady(true);
        return;
      }

      if (!connectionToken || !env) {
        // Only warn if we previously had valid credentials (e.g. token expired/revoked).
        // Suppress the warning during initial OIDC auth loading when token is not yet available.
        if (hadTokenRef.current) {
          logger.warn("Missing credentials, skipping WebSocket initialization");
        }
        return;
      }

      isInitializing = true;
      hadTokenRef.current = true;

      /*
      setFilters(
        subscriptionVariable.filters?.length
          ? subscriptionVariable.filters
          : [`${NOTIFICATION_FILTERS.TEMPLATE_FILTERS}`],
      );
      */

      timeoutId = setTimeout(() => {
        logger.warn("WebSocket connection timeout - continuing without connection");
        setWsReady(true);
      }, CONNECTION_TIMEOUT_MS);

      try {
        if (subscriber) {
          subscriber.stop();
          subscriber = null;
        }

        subscriber = notificationsWebSocketConnection({
          token: connectionToken,
          env,
          variables: subscriptionVariable,
          logger,
          onMessage: (msg) => {
            if (msg.type === "start_ack") {
              if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
              if (msg.id) setSubscriptionId(msg.id);
              setWsReady(true);
              markConnected(connectionToken);
              activeConnectionSignatureRef.current = connectionSignature;
              reconnectAttempts = 0;
              isInitializing = false;
           }
            if (msg.type === "data") {
 
              const dataMsg = msg as NotificationDataMessage;
              const published = dataMsg.payload?.data?.onNotificationPublished;
              const message = typeof published?.message === "string" ? published.message : "";
              const createdBy = typeof published?.createdBy === "string" ? published.createdBy : "";

              let user = sessionStorage.getItem("UUID") || "unknown user";

              const [firstValue = ""] = message
                .split(" - ")
                .map((value) => value.trim());

              if (createdBy != user) {
                invalidateTemplateHtmlCacheEntry(dispatch, firstValue, "Draft");
              }

              processNotificationMessage(msg as Parameters<typeof processNotificationMessage>[0], {
                skipToast: createdBy === user,
              });
            }
          },
          onError: (err) => {
            logger.error("WebSocket error:", err);
            isInitializing = false;
            if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
              reconnectAttempts++;
              logger.warn(`Reconnecting... (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`);
              if (!reconnectTimeoutId && isEffectActive) {
                reconnectTimeoutId = setTimeout(() => {
                  reconnectTimeoutId = null;
                  void initializeConnection();
                }, RECONNECT_DELAY_MS);
              }
            } else {
              logger.error("Max reconnection attempts reached, continuing without WebSocket");
              setWsReady(true);
            }
          },
        });

        if (subscriber) {
          setStopFunction(subscriber.stop);
          setConnectedElement({ stop: subscriber.stop });
        }
      } catch (err) {
        isInitializing = false;
        logger.error("WebSocket connection failed:", err);
        setWsReady(true);
      }
    };

    void initializeConnection();

    const handleBeforeUnload = () => { isPageUnloadingRef.current = true; };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      isEffectActive = false;
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (timeoutId) clearTimeout(timeoutId);
      if (reconnectTimeoutId) clearTimeout(reconnectTimeoutId);
      if (subscriber) subscriber.stop();
      if (!isPageUnloadingRef.current) clearConnected();
      if (!isPageUnloadingRef.current) activeConnectionSignatureRef.current = null;
      setStopFunction(null);
      setConnectedElement(null);
    };
  }, [enabled, env, subscriptionVariable, setStopFunction, setSubscriptionId, setFilters, setConnectedElement, setWsReady, dispatch]);
}
